/*
  Runway section.
  Desktop: the stage pins and vertical scroll drives the rail sideways, eased (lerp) for a soft glide.
  Phones / reduced motion: native horizontal swipe strip.
  Effects (when the section has .rw--motion): centre focus via --ad (0 centred … 1 far),
  velocity tilt via --skew on the rail, and act titles drifting at half speed behind the looks.
*/
(() => {
  const mq = matchMedia('(prefers-reduced-motion: reduce)');
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const pad = (n) => String(n).padStart(2, '0');
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

  function init(show) {
    if (show.dataset.rwReady) return;
    show.dataset.rwReady = '1';
    const q = (s) => show.querySelector(s);
    const rail = q('[data-rw-rail]');
    const track = q('[data-rw-track]');
    const stage = q('[data-rw-stage]');
    const fill = q('[data-rw-fill]');
    const countEl = q('[data-rw-count]');
    const actEl = q('[data-rw-act]');
    const looks = Array.from(show.querySelectorAll('.rw-look'));
    const ghosts = Array.from(show.querySelectorAll('[data-rw-ghost]'));
    const steps = Array.from(show.querySelectorAll('[data-rw-steps] > span'));
    const acts = Array.from(show.querySelectorAll('[data-act-title]'));
    let actOffs = [];
    const motion = show.classList.contains('rw--motion');
    const N = looks.length;
    if (!rail || !track || !N) return;

    let pin = false, max = 0, top0 = 0, offs = [], snapOffs = [], ghostOffs = [], active = -1;
    let x = 0, target = 0, raf = 0, skew = 0;

    function measure() {
      const base = pin ? rail.getBoundingClientRect().left + x : track.getBoundingClientRect().left - track.scrollLeft;
      offs = looks.map((l) => { const r = l.getBoundingClientRect(); return r.left - base + r.width / 2; });
      ghostOffs = ghosts.map((g) => g.parentElement.getBoundingClientRect().left - base);
      actOffs = acts.map((el) => { const r = el.getBoundingClientRect(); return r.left - base + r.width / 2; });
      snapOffs = Array.from(show.querySelectorAll('.rw-look, .rw-act, .rw-end')).map((el) => { const r = el.getBoundingClientRect(); return r.left - base + r.width / 2; });
    }

    function layout() {
      pin = !mq.matches;
      show.classList.toggle('is-pin', pin);
      rail.style.transform = '';
      show.style.height = '';
      x = 0;
      if (pin) {
        max = Math.max(0, rail.scrollWidth - innerWidth);
        // stage is 100svh in CSS: stable while a phone's address bar shows/hides
        show.style.height = `${max + stage.offsetHeight}px`;
      }
      top0 = show.getBoundingClientRect().top + scrollY;
      measure();
      if (window.Silk) window.Silk.renderAll(show);
      target = pin ? clamp(scrollY - top0, 0, max) : track.scrollLeft;
      x = target;
      paint();
    }

    function paint() {
      const view = pin ? innerWidth : track.clientWidth;
      if (pin) rail.style.transform = `translate3d(${-x}px,0,0)`;
      const range = pin ? max : track.scrollWidth - view;
      if (fill) fill.style.setProperty('--p', (range > 0 ? x / range : 0).toFixed(4));

      const c = x + view / 2;
      let best = 0, bd = Infinity;
      offs.forEach((o, i) => {
        const d = o - c;
        if (Math.abs(d) < bd) { bd = Math.abs(d); best = i; }
        if (motion) {
          const n = clamp(d / (view * 0.55), -1, 1);
          looks[i].style.setProperty('--ad', Math.abs(n).toFixed(3));
          looks[i].style.setProperty('--d', n.toFixed(3));
        }
      });
      if (motion) {
        ghosts.forEach((g, i) => { g.style.transform = `translate3d(${((x + view - ghostOffs[i]) * 0.45).toFixed(1)}px,0,0)`; });
        rail.style.setProperty('--skew', `${skew.toFixed(2)}deg`);
      }
      // act cards: show their title in the label while centred, and give them the same --d/--ad as looks
      let actLabel = null;
      acts.forEach((el, i) => {
        const n = clamp((actOffs[i] - c) / (view * 0.55), -1, 1);
        if (motion) { el.style.setProperty('--d', n.toFixed(3)); el.style.setProperty('--ad', Math.abs(n).toFixed(3)); }
        if (Math.abs(n) < 0.5) actLabel = el.dataset.actTitle;
      });
      if (actEl) actEl.textContent = actLabel || (looks[best] && looks[best].dataset.act) || actEl.textContent;
      if (best !== active) {
        if (looks[active]) looks[active].classList.remove('is-on');
        active = best;
        looks[best].classList.add('is-on');
        if (countEl) countEl.innerHTML = `${pad(best + 1)} <span>/ ${pad(N)}</span>`;
        steps.forEach((st, j) => { st.classList.toggle('is-done', j < best); st.classList.toggle('is-on', j === best); });
      }
    }

    function tick() {
      raf = 0;
      const diff = target - x;
      if (pin && motion && !reduce.matches) {
        x += diff * 0.12;
        skew = clamp(diff * 0.01, -6, 6);
        if (Math.abs(diff) < 0.3) { x = target; skew = 0; }
      } else {
        x = target; skew = 0;
      }
      paint();
      if (x !== target) raf = requestAnimationFrame(tick);
    }
    const kick = () => {
      target = pin ? clamp(scrollY - top0, 0, max) : track.scrollLeft;
      if (!raf) raf = requestAnimationFrame(tick);
    };

    function goTo(i) {
      i = clamp(i, 0, N - 1);
      const behavior = reduce.matches ? 'auto' : 'smooth';
      if (pin) {
        scrollTo({ top: top0 + clamp(offs[i] - innerWidth / 2, 0, max), behavior });
      } else {
        track.scrollTo({ left: offs[i] - track.clientWidth / 2, behavior });
        const r = track.getBoundingClientRect();
        if (r.top < 0 || r.bottom > innerHeight) scrollTo({ top: scrollY + r.top - 80, behavior });
      }
    }

    addEventListener('scroll', kick, { passive: true });

    // touch screens: when the finger stops inside the show, settle on the nearest look
    const coarse = matchMedia('(pointer: coarse)');
    let snapT, snapping = false;
    const settle = () => {
      if (!pin || !coarse.matches || snapping) return;
      const t = scrollY - top0;
      if (t <= 4 || t >= max - 4) return;
      const c = t + innerWidth / 2;
      let best = snapOffs[0], bd = Infinity;
      snapOffs.forEach((o) => { const d = Math.abs(o - c); if (d < bd) { bd = d; best = o; } });
      const goal = clamp(best - innerWidth / 2, 0, max);
      if (Math.abs(goal - t) < 6) return;
      snapping = true;
      scrollTo({ top: top0 + goal, behavior: reduce.matches ? 'auto' : 'smooth' });
      setTimeout(() => { snapping = false; }, 650);
    };
    addEventListener('scroll', () => { clearTimeout(snapT); snapT = setTimeout(settle, 160); }, { passive: true });
    track.addEventListener('scroll', kick, { passive: true });
    let rt, lastW = innerWidth;
    addEventListener('resize', () => {
      // phones fire resize when the address bar collapses; only re-layout on real width changes
      if (innerWidth === lastW && matchMedia('(pointer: coarse)').matches) return;
      lastW = innerWidth;
      clearTimeout(rt); rt = setTimeout(layout, 150);
    });
    mq.addEventListener('change', layout);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(layout);
    addEventListener('load', layout);

    stage.addEventListener('wheel', (e) => {
      if (!pin || Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      scrollBy(0, e.deltaX);
    }, { passive: false });

    addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      if (e.target.closest('input,select,textarea,[contenteditable]')) return;
      const r = show.getBoundingClientRect();
      if (r.bottom < innerHeight * 0.5 || r.top > innerHeight * 0.5) return;
      e.preventDefault();
      goTo(active + (e.key === 'ArrowRight' ? 1 : -1));
    });
    show.querySelectorAll('[data-rw-step]').forEach((b) => b.addEventListener('click', () => goTo(active + Number(b.dataset.rwStep))));
    show.addEventListener('click', (e) => {
      const a = e.target.closest('[data-rw-goto]');
      if (!a) return;
      e.preventDefault();
      goTo(Number(a.dataset.rwGoto));
    });

    layout();
    requestAnimationFrame(() => show.classList.add('is-ready'));
  }

  const boot = () => document.querySelectorAll('[data-runway]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', (e) => { const el = e.target.querySelector('[data-runway]'); if (el) init(el); });
})();
